import { useState } from 'react'
import { useTranslate } from '@ur-solutions/react-hooks'

import { format } from '@/utils/date-fns'
import { FAIcon } from '@/components/fa-icon/fa-icon'

import { formatValue, humanizeFieldKey, type ChangedField } from './util'

/** Best-effort label for a model field key, reusing existing common/issues strings. */
function useFieldLabel() {
  const t = useTranslate({
    severity: 'common.severity',
    category: 'common.category',
    subcategory: 'common.sub-category',
    responsibleUser: 'common.responsible-user',
    caseHandler: 'common.case-handler',
    status: 'common.status',
    solution: 'issues.final-solution',
    title: 'common.title',
    description: 'common.description',
    actualCost: 'issues.actual-cost',
    costEstimation: 'issues.cost-estimation',
    tags: 'common.tags',
  })

  const map: Record<string, string> = {
    severity: t.severity,
    category_id: t.category,
    subcategory_id: t.subcategory,
    responsible_user_id: t.responsibleUser,
    case_handler_id: t.caseHandler,
    closed_by_id: t.status,
    solution: t.solution,
    title: t.title,
    issue_description: t.description,
    actual_cost: t.actualCost,
    cost_estimation: t.costEstimation,
    tags: t.tags,
  }

  return (key: string): string => map[key] ?? humanizeFieldKey(key)
}

const formatDate = (iso: string) => format(new Date(iso), 'PP p')

const DiffRow = ({
  label,
  value,
  yes,
  no,
}: {
  label: string
  value: ChangedField
  yes: string
  no: string
}) => {
  const oldValue = formatValue(value.old_value, { yes, no, formatDate })
  const newValue = formatValue(value.new_value, { yes, no, formatDate })

  return (
    <div className="flex items-center gap-3 border-b border-gray-200 px-3 py-2.5 last:border-b-0">
      <span className="w-28 shrink-0 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-1 items-center gap-2 text-sm">
        {oldValue !== null ? (
          <span className="text-tertiary line-through">{oldValue}</span>
        ) : (
          <span className="text-tertiary">—</span>
        )}
        <FAIcon icon="arrow-right" className="text-xs text-tertiary" />
        {newValue !== null ? (
          <span className="font-medium text-primary">{newValue}</span>
        ) : (
          <span className="text-tertiary">—</span>
        )}
      </div>
    </div>
  )
}

/** Collapsible "N fields changed" diff: a toggle button + the old→new rows. */
export const ActionLogDiff = ({
  fields,
}: {
  fields: [string, ChangedField][]
}) => {
  const [expanded, setExpanded] = useState(false)
  const fieldLabel = useFieldLabel()

  const t = useTranslate({
    yes: 'common.yes',
    no: 'common.no',
    fieldsChanged: ['issues.action-log.fields-changed', { count: 'count' }],
  })

  return (
    <>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded(e => !e)}
        className="flex w-fit items-center gap-1.5 rounded-md border border-gray-200 bg-quaternary px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:brightness-95"
      >
        <FAIcon icon="list" className="text-xs" />
        {t.fieldsChanged({ count: String(fields.length) })}
        <FAIcon icon={expanded ? 'chevron-up' : 'chevron-down'} />
      </button>

      {expanded && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-quaternary">
          {fields.map(([field, value]) => (
            <DiffRow
              key={field}
              label={fieldLabel(field)}
              value={value}
              yes={t.yes}
              no={t.no}
            />
          ))}
        </div>
      )}
    </>
  )
}
